import React, { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../../lib/api";
import { useSettings } from "../../lib/useSettings";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Checkbox } from "../../components/ui/checkbox";
import { Slider } from "../../components/ui/slider";
import {
  Loader2, ArrowRight, ArrowLeft, CheckCircle2, Video, Globe, Camera,
  Sparkles, Mail, User, Phone, Send,
} from "lucide-react";
import { toast } from "sonner";

const ICONS = { Video, Globe, Camera, Sparkles };

/**
 * DynamicQuoteFormPage — Phase 33i
 * Page publique /devis qui :
 * - Sans param `?template=...` : affiche un sélecteur de templates
 * - Avec `?template=video-entreprise` : affiche le formulaire dynamique correspondant
 * Les valeurs sont stockées dans `meta.custom_fields` du lead pour traçabilité.
 */
export default function DynamicQuoteFormPage() {
  const { settings: s } = useSettings();
  const [searchParams, setSearchParams] = useSearchParams();
  const slug = searchParams.get("template") || "";

  const [templates, setTemplates] = useState([]);
  const [tpl, setTpl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  // Coordonnées contact (toujours requises)
  const [contact, setContact] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  // Champs dynamiques
  const [values, setValues] = useState({});
  // Wizard step (1=contact, 2=détails, 3=récap)
  const [step, setStep] = useState(1);
  const [submittedNumber, setSubmittedNumber] = useState("");

  useEffect(() => {
    setLoading(true);
    if (slug) {
      api.get(`/quote-forms/${slug}`)
        .then(r => {
          setTpl(r.data);
          // Init valeurs avec defaults
          const init = {};
          (r.data.fields || []).forEach(f => {
            if (f.default !== null && f.default !== undefined) init[f.key] = f.default;
            else if (f.type === "multiselect") init[f.key] = [];
            else if (f.type === "checkbox") init[f.key] = false;
            else init[f.key] = "";
          });
          setValues(init);
        })
        .catch(() => setTpl(false))
        .finally(() => setLoading(false));
    } else {
      api.get("/quote-forms")
        .then(r => setTemplates(r.data || []))
        .finally(() => setLoading(false));
    }
  }, [slug]);

  const setVal = (key, val) => setValues(v => ({ ...v, [key]: val }));

  const sortedFields = useMemo(() => {
    if (!tpl) return [];
    return [...(tpl.fields || [])].sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [tpl]);

  // Phase 0 — Évalue show_if : retourne true si le champ doit être visible
  const isFieldVisible = (f) => {
    const cond = f.show_if;
    if (!cond || !cond.field) return true;
    const v = values[cond.field];
    const arr = Array.isArray(cond.values) ? cond.values : [];
    switch (cond.op) {
      case "eq": return v === arr[0];
      case "neq": return v !== arr[0];
      case "in": return arr.includes(v);
      case "not_in": return !arr.includes(v);
      case "contains":
        if (Array.isArray(v)) return arr.some(x => v.includes(x));
        return arr.some(x => String(v || "").includes(x));
      case "truthy": return !!v && (!Array.isArray(v) || v.length > 0);
      case "falsy": return !v || (Array.isArray(v) && v.length === 0);
      default: return true;
    }
  };

  const visibleFields = useMemo(() => sortedFields.filter(isFieldVisible), [sortedFields, values]); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!contact.name || !contact.email || !contact.phone) {
      toast.error("Nom, email et téléphone sont requis");
      return;
    }
    // Vérif champs requis du template (uniquement les visibles)
    for (const f of visibleFields) {
      if (f.required) {
        const v = values[f.key];
        if (v === "" || v === null || v === undefined ||
            (Array.isArray(v) && v.length === 0)) {
          toast.error(`Champ requis manquant : ${f.label}`);
          return;
        }
      }
    }
    // Build payload : on n'envoie QUE les champs visibles (les conditionnels masqués sont exclus)
    const visibleKeys = new Set(visibleFields.map(f => f.key));
    const filteredValues = Object.fromEntries(
      Object.entries(values).filter(([k]) => visibleKeys.has(k))
    );
    setSubmitting(true);
    try {
      const r = await api.post("/quote-requests", {
        template_slug: tpl.slug,
        contact: {
          name: contact.name,
          email: contact.email,
          phone: contact.phone,
          company: contact.company || "",
        },
        custom_fields: filteredValues,
        message: contact.message || "",
        page: window.location.pathname + window.location.search,
      });
      setSubmittedNumber(r.data?.number || "");
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur — réessayez plus tard");
    } finally { setSubmitting(false); }
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin text-slate-400"/></div>;
  }

  // ============ ÉCRAN 1 : Sélecteur de templates ============
  if (!slug) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-16" data-testid="quote-template-picker">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-10">
            <div className="text-xs uppercase tracking-[0.3em] font-bold text-sky-600 mb-3">Demande de devis</div>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 mb-4">De quoi avez-vous besoin ?</h1>
            <p className="text-base text-slate-600 max-w-2xl mx-auto">
              Choisissez le formulaire adapté à votre projet pour recevoir un devis précis et personnalisé sous 24h.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-5" data-testid="quote-template-grid">
            {templates.map(t => {
              const Icon = ICONS[t.icon] || Sparkles;
              return (
                <Link
                  key={t.slug}
                  to={`/devis?template=${t.slug}`}
                  className="group relative bg-white rounded-2xl border-2 border-slate-200 p-7 hover:border-sky-400 hover:shadow-2xl hover:-translate-y-1 transition-all"
                  data-testid={`quote-template-${t.slug}`}
                >
                  <div className="w-12 h-12 rounded-2xl bg-sky-50 group-hover:bg-sky-500 flex items-center justify-center mb-4 transition-colors">
                    <Icon className="w-6 h-6 text-sky-600 group-hover:text-white transition-colors"/>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-1">{t.label}</h3>
                  <div className="text-[11px] uppercase tracking-wider font-semibold text-sky-600 mb-3">{t.sector}</div>
                  <p className="text-sm text-slate-600 leading-relaxed mb-5">{t.description}</p>
                  <div className="text-sm font-bold text-slate-900 inline-flex items-center gap-1.5 group-hover:text-sky-700">
                    {(t.fields || []).length} questions ciblées
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform"/>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="text-center mt-10 text-sm text-slate-500">
            Vous ne trouvez pas votre catégorie ? <Link to="/contact" className="text-sky-700 font-semibold underline">Décrivez-nous votre projet directement</Link>.
          </div>
        </div>
      </div>
    );
  }

  // ============ ÉCRAN 2 : Template introuvable ============
  if (tpl === false) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <Card className="p-8 max-w-md text-center">
          <div className="text-5xl mb-3">🤔</div>
          <h2 className="text-xl font-bold text-slate-900 mb-2">Formulaire introuvable</h2>
          <p className="text-sm text-slate-600 mb-5">Le formulaire « {slug} » n'existe pas ou n'est plus disponible.</p>
          <Link to="/devis" className="inline-flex items-center gap-1.5 text-sky-700 font-semibold hover:underline">
            <ArrowLeft className="w-4 h-4"/> Voir les formulaires disponibles
          </Link>
        </Card>
      </div>
    );
  }

  // ============ ÉCRAN 3 : Confirmation envoi ============
  if (done) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white flex items-center justify-center p-6">
        <Card className="p-10 max-w-lg text-center bg-white border-0 shadow-2xl rounded-3xl" data-testid="quote-success">
          <div className="w-20 h-20 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-5">
            <CheckCircle2 className="w-10 h-10 text-emerald-600"/>
          </div>
          <h2 className="text-3xl font-black text-slate-900 mb-2">Demande reçue !</h2>
          {submittedNumber && (
            <div className="inline-block px-4 py-2 rounded-xl bg-sky-50 border border-sky-200 mb-4" data-testid="quote-success-number">
              <div className="text-[10px] uppercase tracking-wider text-sky-700 font-bold mb-0.5">Référence</div>
              <div className="font-mono text-xl font-black text-sky-900">{submittedNumber}</div>
            </div>
          )}
          <p className="text-base text-slate-600 leading-relaxed mb-6">
            Merci {contact.name}, votre demande pour <strong>{tpl.label}</strong> a bien été enregistrée.
            Un email récapitulatif a été envoyé à <strong>{contact.email}</strong>.
            Notre équipe vous recontactera sous <strong className="text-emerald-700">24h ouvrées</strong>.
          </p>
          <div className="flex items-center justify-center gap-3 flex-wrap">
            <Link to="/" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm transition">
              <ArrowLeft className="w-4 h-4"/> Retour à l'accueil
            </Link>
            <Link to="/devis" className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full border-2 border-slate-300 hover:border-slate-900 text-slate-700 hover:text-slate-900 font-semibold text-sm transition">
              Faire une autre demande
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  // ============ ÉCRAN 4 : Wizard 3 étapes ============
  const Icon = ICONS[tpl.icon] || Sparkles;

  // Validation par étape
  const canGoStep2 = !!contact.name && !!contact.email && !!contact.phone;
  const canGoStep3 = visibleFields.every(f => {
    if (!f.required) return true;
    const v = values[f.key];
    return !(v === "" || v === null || v === undefined || (Array.isArray(v) && v.length === 0));
  });

  const goNext = () => {
    if (step === 1 && !canGoStep2) {
      toast.error("Nom, email et téléphone sont requis");
      return;
    }
    if (step === 2 && !canGoStep3) {
      toast.error("Merci de remplir tous les champs requis");
      return;
    }
    setStep(s => s + 1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const goBack = () => {
    setStep(s => Math.max(1, s - 1));
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const STEPS = [
    { n: 1, label: "Coordonnées" },
    { n: 2, label: "Détails projet" },
    { n: 3, label: "Récapitulatif" },
  ];
  const progressPct = ((step - 1) / (STEPS.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white py-10" data-testid={`quote-form-${tpl.slug}`}>
      <div className="max-w-3xl mx-auto px-6">
        <Link to="/devis" className="inline-flex items-center gap-1.5 mb-5 text-xs font-semibold text-slate-500 hover:text-slate-900">
          <ArrowLeft className="w-3.5 h-3.5"/> Choisir un autre formulaire
        </Link>

        {/* Header template */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-7 mb-5">
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 rounded-2xl bg-sky-50 flex items-center justify-center flex-shrink-0">
              <Icon className="w-7 h-7 text-sky-600"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[11px] uppercase tracking-wider font-bold text-sky-600 mb-1">{tpl.sector}</div>
              <h1 className="text-2xl md:text-3xl font-black text-slate-900 mb-1">{tpl.label}</h1>
              <p className="text-sm text-slate-600">{tpl.description}</p>
            </div>
          </div>
        </div>

        {/* Stepper */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 mb-5" data-testid="quote-stepper">
          <div className="flex items-center justify-between mb-3">
            {STEPS.map((s, idx) => (
              <React.Fragment key={s.n}>
                <div className={`flex items-center gap-2 ${step >= s.n ? "text-sky-700" : "text-slate-400"}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    step > s.n ? "bg-emerald-500 text-white" :
                    step === s.n ? "bg-sky-500 text-white ring-4 ring-sky-100" :
                    "bg-slate-200 text-slate-500"
                  }`} data-testid={`step-${s.n}-indicator`}>
                    {step > s.n ? <CheckCircle2 className="w-4 h-4"/> : s.n}
                  </div>
                  <span className="text-xs font-semibold hidden sm:inline">{s.label}</span>
                </div>
                {idx < STEPS.length - 1 && (
                  <div className="flex-1 h-1 bg-slate-200 mx-2 rounded-full overflow-hidden">
                    <div className="h-full bg-emerald-500 transition-all" style={{ width: step > s.n ? "100%" : "0%" }}/>
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <div className="h-full bg-gradient-to-r from-sky-500 to-emerald-500 transition-all" style={{ width: `${progressPct}%` }}/>
          </div>
        </div>

        <form onSubmit={(e) => e.preventDefault()} className="space-y-5" data-testid="quote-dynamic-form">
          {/* STEP 1 — Coordonnées */}
          {step === 1 && (
            <Card className="p-6" data-testid="wizard-step-1">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Étape 1/3</div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Vos coordonnées</h2>
              <p className="text-sm text-slate-500 mb-5">Pour pouvoir vous recontacter avec votre devis personnalisé.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-xs font-semibold">Nom complet *</Label>
                  <div className="relative mt-1.5">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                    <Input value={contact.name} onChange={e => setContact(c => ({...c, name: e.target.value}))} required className="pl-9" data-testid="quote-contact-name" autoFocus/>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Société</Label>
                  <Input value={contact.company} onChange={e => setContact(c => ({...c, company: e.target.value}))} className="mt-1.5" placeholder="(facultatif)" data-testid="quote-contact-company"/>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Email *</Label>
                  <div className="relative mt-1.5">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                    <Input type="email" value={contact.email} onChange={e => setContact(c => ({...c, email: e.target.value}))} required className="pl-9" data-testid="quote-contact-email"/>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-semibold">Téléphone *</Label>
                  <div className="relative mt-1.5">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400"/>
                    <Input type="tel" value={contact.phone} onChange={e => setContact(c => ({...c, phone: e.target.value}))} required className="pl-9" data-testid="quote-contact-phone"/>
                  </div>
                </div>
              </div>
            </Card>
          )}

          {/* STEP 2 — Détails projet */}
          {step === 2 && (
            <Card className="p-6" data-testid="wizard-step-2">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Étape 2/3</div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Détails de votre projet</h2>
              <p className="text-sm text-slate-500 mb-5">Plus vous êtes précis, plus le devis sera juste et rapide.</p>
              <div className="space-y-5">
                {visibleFields.map(f => (
                  <DynamicField key={f.key} field={f} value={values[f.key]} onChange={v => setVal(f.key, v)}/>
                ))}
              </div>
            </Card>
          )}

          {/* STEP 3 — Récapitulatif */}
          {step === 3 && (
            <Card className="p-6" data-testid="wizard-step-3">
              <div className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-1">Étape 3/3</div>
              <h2 className="text-xl font-bold text-slate-900 mb-1">Récapitulatif</h2>
              <p className="text-sm text-slate-500 mb-5">Vérifiez les informations avant l'envoi. Vous recevrez une copie de cette demande par email.</p>
              <RecapBlock title="Vos coordonnées" rows={[
                ["Nom", contact.name],
                ["Société", contact.company],
                ["Email", contact.email],
                ["Téléphone", contact.phone],
              ]}/>
              <RecapBlock title="Détails du projet" rows={visibleFields.map(f => [
                f.label,
                (() => {
                  const v = values[f.key];
                  if (Array.isArray(v)) return v.join(", ") || "—";
                  if (v === true) return "Oui";
                  if (v === false) return "Non";
                  if (v === "" || v === null || v === undefined) return "—";
                  return String(v);
                })(),
              ])}/>
              <div>
                <Label className="text-xs font-semibold">Message complémentaire (facultatif)</Label>
                <Textarea
                  value={contact.message} onChange={e => setContact(c => ({...c, message: e.target.value}))}
                  rows={3} placeholder="Toute info supplémentaire utile…" className="mt-1.5"
                  data-testid="quote-contact-message"
                />
              </div>
            </Card>
          )}

          {/* Navigation */}
          <div className="flex items-center justify-between gap-3 flex-wrap">
            {step > 1 ? (
              <Button type="button" variant="outline" onClick={goBack} className="rounded-full" data-testid="wizard-back">
                <ArrowLeft className="w-4 h-4 mr-1.5"/> Retour
              </Button>
            ) : <div/>}
            {step < 3 ? (
              <Button type="button" onClick={goNext} className="rounded-full bg-slate-900 hover:bg-slate-800 px-6" data-testid="wizard-next">
                Continuer <ArrowRight className="w-4 h-4 ml-1.5"/>
              </Button>
            ) : (
              <Button type="button" onClick={submit} disabled={submitting}
                className="rounded-full bg-emerald-600 hover:bg-emerald-700 px-6 h-11" data-testid="quote-form-submit">
                {submitting ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin"/> : <Send className="w-4 h-4 mr-1.5"/>}
                {submitting ? "Envoi en cours…" : "Envoyer ma demande"}
              </Button>
            )}
          </div>
          <p className="text-xs text-slate-500 text-center">
            En envoyant ce formulaire, vous acceptez d'être recontacté(e) par {s?.agency_brand_name || "notre équipe"} sous 24h ouvrées.
          </p>
        </form>
      </div>
    </div>
  );
}

/**
 * RecapBlock — affiche une liste label/valeur en bloc dans le récap.
 */
function RecapBlock({ title, rows }) {
  const filteredRows = rows.filter(([, v]) => v !== "" && v !== null && v !== undefined && v !== "—");
  if (filteredRows.length === 0) return null;
  return (
    <div className="mb-4 rounded-xl border border-slate-200 overflow-hidden bg-slate-50">
      <div className="px-4 py-2 bg-slate-100 border-b border-slate-200 text-[10px] uppercase tracking-wider font-bold text-slate-600">{title}</div>
      <dl className="divide-y divide-slate-200">
        {filteredRows.map(([k, v], i) => (
          <div key={i} className="px-4 py-2.5 grid grid-cols-3 gap-2">
            <dt className="text-xs text-slate-500 col-span-1">{k}</dt>
            <dd className="text-sm text-slate-900 font-medium col-span-2 break-words">{v}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

/**
 * DynamicField — rend un champ selon son type. Wraps UI components.
 */
function DynamicField({ field: f, value, onChange }) {
  const helpEl = f.helptext ? <p className="text-xs text-slate-500 mt-1">{f.helptext}</p> : null;

  if (f.type === "textarea") {
    return (
      <div>
        <Label className="text-sm font-semibold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
        <Textarea
          value={value || ""} onChange={e => onChange(e.target.value)}
          rows={f.rows || 4} placeholder={f.placeholder} className="mt-1.5"
          data-testid={`field-${f.key}`}
        />
        {helpEl}
      </div>
    );
  }

  if (f.type === "select") {
    return (
      <div>
        <Label className="text-sm font-semibold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
        <select
          value={value || ""} onChange={e => onChange(e.target.value)}
          className="mt-1.5 w-full h-10 rounded-md border border-slate-300 bg-white px-3 text-sm focus:border-sky-500 focus:ring-2 focus:ring-sky-500/20 outline-none"
          data-testid={`field-${f.key}`}
        >
          <option value="">— Choisir —</option>
          {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        {helpEl}
      </div>
    );
  }

  if (f.type === "radio") {
    return (
      <div>
        <Label className="text-sm font-semibold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`field-${f.key}`}>
          {(f.options || []).map(o => (
            <label key={o} className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer text-sm transition ${value === o ? "border-sky-500 bg-sky-50 text-sky-900" : "border-slate-200 hover:border-slate-300"}`}>
              <input type="radio" name={f.key} value={o} checked={value === o} onChange={() => onChange(o)} className="accent-sky-600"/>
              <span>{o}</span>
            </label>
          ))}
        </div>
        {helpEl}
      </div>
    );
  }

  if (f.type === "multiselect") {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (o) => onChange(arr.includes(o) ? arr.filter(x => x !== o) : [...arr, o]);
    return (
      <div>
        <Label className="text-sm font-semibold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
        <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2" data-testid={`field-${f.key}`}>
          {(f.options || []).map(o => (
            <label key={o} className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer text-sm transition ${arr.includes(o) ? "border-sky-500 bg-sky-50 text-sky-900" : "border-slate-200 hover:border-slate-300"}`}>
              <input type="checkbox" checked={arr.includes(o)} onChange={() => toggle(o)} className="accent-sky-600"/>
              <span>{o}</span>
            </label>
          ))}
        </div>
        {helpEl}
      </div>
    );
  }

  if (f.type === "checkbox") {
    return (
      <div data-testid={`field-${f.key}`}>
        <label className="flex items-start gap-2 cursor-pointer">
          <Checkbox checked={!!value} onCheckedChange={onChange} className="mt-0.5"/>
          <span className="text-sm font-semibold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</span>
        </label>
        {helpEl}
      </div>
    );
  }

  if (f.type === "slider") {
    const min = f.min ?? 0, max = f.max ?? 10, step = f.step ?? 1;
    const cur = typeof value === "number" ? value : (f.default ?? min);
    return (
      <div data-testid={`field-${f.key}`}>
        <div className="flex items-center justify-between mb-2">
          <Label className="text-sm font-semibold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
          <span className="text-sm font-bold text-sky-700 px-2 py-0.5 rounded bg-sky-50">{cur}</span>
        </div>
        <Slider value={[cur]} onValueChange={(v) => onChange(v[0])} min={min} max={max} step={step}/>
        <div className="flex items-center justify-between text-[11px] text-slate-400 mt-1">
          <span>{min}</span><span>{max}</span>
        </div>
        {helpEl}
      </div>
    );
  }

  if (f.type === "date" || f.type === "datetime") {
    return (
      <div>
        <Label className="text-sm font-semibold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
        <Input
          type={f.type === "datetime" ? "datetime-local" : "date"}
          value={value || ""} onChange={e => onChange(e.target.value)}
          className="mt-1.5" data-testid={`field-${f.key}`}
        />
        {helpEl}
      </div>
    );
  }

  if (f.type === "number") {
    return (
      <div>
        <Label className="text-sm font-semibold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
        <Input
          type="number" value={value === "" ? "" : (value ?? "")} onChange={e => onChange(e.target.value === "" ? "" : Number(e.target.value))}
          min={f.min} max={f.max} step={f.step || 1} placeholder={f.placeholder}
          className="mt-1.5" data-testid={`field-${f.key}`}
        />
        {helpEl}
      </div>
    );
  }

  // text / email / tel / url (default)
  return (
    <div>
      <Label className="text-sm font-semibold">{f.label}{f.required && <span className="text-rose-500"> *</span>}</Label>
      <Input
        type={["email", "tel", "url"].includes(f.type) ? f.type : "text"}
        value={value || ""} onChange={e => onChange(e.target.value)}
        placeholder={f.placeholder} className="mt-1.5"
        data-testid={`field-${f.key}`}
      />
      {helpEl}
    </div>
  );
}
